/**
 * ENG-9068: a rejected `pollCycle()` must not permanently stop the poll loop.
 *
 * WHAT BREAKS WITHOUT THIS
 * ------------------------
 * All three entrypoints into the manager poll loop looked like this:
 *
 *     void pollCycle().then(() => { scheduleNext(); });
 *
 * `scheduleNext()` — the call that re-arms the poll timer — lives INSIDE the
 * `.then()`. A rejected `pollCycle()` therefore skips it, and nothing ever
 * re-arms the timer. The manager process stays alive, its heartbeat keeps
 * reporting, and it simply never polls again. That is the worst shape of
 * failure we have: an agent that looks healthy on every dashboard and does
 * nothing.
 *
 * WHY A BARE `.catch(log)` WOULD NOT FIX IT
 * -----------------------------------------
 * This is the trap, and it is why this module exists instead of three inline
 * `.catch()` calls. Appending `.catch(log)` to those expressions silences the
 * unhandled rejection and leaves the loop just as dead, because the rejection
 * still bypasses the `.then()` that owns `scheduleNext()`. The fix is not
 * "handle the error", it is "re-arm on BOTH paths" — which is what the
 * `finally` below guarantees.
 *
 * HOW REACHABLE IS THE REJECTION?
 * -------------------------------
 * Narrower than "any poll failure", and worth stating precisely so nobody
 * later reads this guard as protecting more than it does. `pollCycleInner`
 * wraps its main body (try at :7215, catch at :8764), so ordinary API, network
 * and 5xx failures are already caught there and drive `consecutivePollFailures`
 * and the ENG-5041 backoff. They never reach here. What DOES reach here:
 *
 *   1. A SYNCHRONOUS throw from one of the fire-and-forget calls
 *      (`checkAndUpdateCli`, `maybeUpgradeClaudeCode`,
 *      `maybeProbeClaudeRuntimeAuth`). Each has a `.catch()`, but a `.catch()`
 *      only attaches to a promise that was successfully returned — a function
 *      that throws before returning one bypasses it entirely.
 *   2. A throw inside the outer catch HANDLER itself. It calls `send()`, which
 *      calls `log()`, whose final `process.stderr.write` is unguarded (EPIPE /
 *      EBADF on a closed or exhausted stderr). This is the nastiest lane
 *      because the handler runs precisely when things are already going wrong,
 *      so a host having a bad day is exactly the host that trips it.
 *   3. The region above the first `try`.
 *
 * The codebase already knows about this hazard and has been mitigating it
 * call-site by call-site — see the comment above `runPendingForcedUpdate`:
 * "pollCycle has no process-level rejection handler, so an unexpected throw
 * here would reject the whole cycle." Every such guard is a local patch for a
 * missing global one. This is the global one.
 *
 * Kept in its own module, pure and dependency-injected, for the same reason
 * ENG-9046 extracted `single-flight.ts`: `manager-worker.ts` is ~17k lines of
 * module-level state and nothing inside it is reachable from a unit test.
 *
 * THE OTHER HALF (ENG-9637): RE-ARM ON BOTH PATHS, BUT ONCE PER PASS
 * ------------------------------------------------------------------
 * "Re-arm on BOTH paths" is about success-vs-failure. It was silently also
 * read as "re-arm for every CALLER", and that second reading is what cost
 * money: `pollCycle` is single-flighted, so several callers can await the same
 * pass, and each one re-armed. `scheduleNext()` assigns `pollTimer` without
 * clearing it, so the extra timer stayed armed and untracked — a permanent
 * second poll chain that only a process restart could remove.
 *
 * So the invariant is sharpened, not weakened: at least one re-arm per pass,
 * and no more than one. The primary caller owns it; a coalesced caller
 * declines. Every entrypoint in `manager-worker.ts` (startup, `scheduleNext`
 * timer, `triggerEarlyPoll`) passes through here, so whichever of them is the
 * primary re-arms and the loop cannot stop.
 *
 * A cycle that THROWS still re-arms unconditionally, because a rejection means
 * we never learned whether we were coalesced. Choosing to re-arm there is
 * deliberate: an unnecessary extra chain is a cost bug, a missing one is a
 * silently dead manager, and ENG-9068 exists because the second is far worse.
 */

/**
 * ENG-9637: what the cycle reported about itself.
 *
 * Structural rather than an import of `SingleFlightRunResult`, so this module
 * stays dependency-free and testable with a plain object — the same reason its
 * callbacks are injected. A cycle that is not single-flighted simply returns
 * nothing and is always treated as the owner of its own pass.
 */
export interface PollCycleOutcome {
  /** True when this call rode along on a pass another caller already owns. */
  coalesced: boolean;
}

export interface PollLoopGuardOptions {
  /**
   * Report a rejection. Must not be relied on to be safe — it is wrapped.
   *
   * Typed `void | Promise<void>` and AWAITED. Both of today's callers are
   * synchronous, so this is hardening rather than a live fix — but TypeScript
   * lets a `() => Promise<void>` be assigned to a `() => void` slot (the void
   * return-type special case), so a future caller could pass an `async`
   * function here and get a silently unhandled rejection with no type error to
   * warn them. Awaiting routes it through the same containment below.
   * (CodeRabbit, #4738)
   */
  onError: (err: unknown) => void | Promise<void>;
  /**
   * Re-arm the loop (`scheduleNext`) and run any post-cycle work. Called on
   * BOTH the success and the failure path — that is the entire point.
   *
   * ENG-9637: called AT MOST ONCE PER PASS. When several callers await the
   * same single-flighted cycle, only the one that owns it re-arms; the others
   * report `coalesced: true` and are skipped. Both halves matter — re-arming
   * on only one path stops the loop, re-arming for every caller forks it.
   *
   * Also awaited, for the same reason. An async `reschedule` that rejects must
   * reject this guard's promise rather than vanish, which is the documented
   * behaviour for the synchronous case and should not change with the shape of
   * the callback.
   */
  reschedule: () => void | Promise<void>;
}

/**
 * Run one poll cycle and guarantee the loop is re-armed afterwards.
 *
 * Resolves on both the success and the failure path, so the `void` at the call
 * sites cannot produce an unhandled rejection.
 *
 * The one case this does NOT absorb is `reschedule` itself throwing, and that
 * is deliberate rather than an oversight. `reschedule` is `scheduleNext`, whose
 * job is to arm a timer; if that throws there is no timer to fall back on and
 * swallowing it would hide a real defect behind a silent, permanently-stopped
 * loop — the exact failure this module exists to make impossible to reach
 * quietly. Better an unhandled rejection somebody can see in the log.
 */
export async function pollCycleAndReschedule(
  pollCycle: () => Promise<PollCycleOutcome | void>,
  opts: PollLoopGuardOptions,
): Promise<void> {
  // Explicitly `undefined` rather than left unassigned: `finally` runs on the
  // throw path too, where TypeScript cannot prove an assignment happened, and
  // an unassigned read is a compile error (TS2454). The initial value is also
  // the semantically right one — "we don't know whether this call was
  // coalesced" must fall to the safe side, which is re-arming.
  let outcome: PollCycleOutcome | void = undefined;
  try {
    outcome = await pollCycle();
  } catch (err) {
    // The error reporter is itself wrapped. `onError` bottoms out in `log()`,
    // and lane 2 above is precisely "the logging path threw" — letting a
    // failure in error REPORTING kill the loop would reproduce this bug one
    // level up, which is not a theoretical concern given how we got here.
    //
    // `await` so an async `onError` that rejects is contained here too rather
    // than escaping as an unhandled rejection (CodeRabbit, #4738).
    try {
      await opts.onError(err);
    } catch {
      /* deliberately empty: nothing left to report with */
    }
  } finally {
    // `finally`, not a tail call after the try/catch: this must run even if
    // `onError` rethrows something the inner catch cannot see, and even if a
    // future edit adds an early `return` above.
    //
    // Awaited, and deliberately NOT wrapped — an async `reschedule` that
    // rejects must reject this promise exactly as a synchronous throw does.
    // See the non-goal in the docstring above: a failure to arm the timer is
    // the one thing that must stay loud.
    //
    // ENG-9637: skipped only when we positively know this call was coalesced.
    // `undefined` (a throw, or a cycle that reports nothing) re-arms, so the
    // loop can never stop for want of information.
    if (!outcome?.coalesced) {
      await opts.reschedule();
    }
  }
}
